import { useState } from 'react'
import type { ReactElement } from 'react'

import type { HeatLevel } from '@wasp/heat'

import styles from './RacionadoSettingsPage.module.scss'
import { markValue } from './ThresholdRuler'

/** What the example card shows, worked out from the values on the form. */
export type PreviewCard = {
  title: string
  detail: string
  exclamations: 0 | 1 | 2 | 3
  /** The heat bar, on the hot board's cards. */
  heat?: { value: number; level: HeatLevel }
  /** Past its target: «Tarde». */
  late?: boolean
  /** The cold room's band. */
  band?: string
}

const LEVEL_LABEL: Record<HeatLevel, string> = {
  frio: 'frío',
  templado: 'templado',
  caliente: 'caliente',
  ardiente: 'ardiente',
  llamas: 'en llamas',
}

type RacionadoPreviewProps = {
  /** The board («caliente»): it names the example's field. */
  board: string
  /** «Así se ve un semi que salió de cocina hace», before the example's minutes. */
  caption: string
  unit: 'min' | 'h'
  initialSample: number
  /** The card for an example; null while a value it needs does not parse. */
  cardFor: (sample: number) => PreviewCard | null
}

/**
 * A card of the board drawn with the values on the form, not the saved ones:
 * change a mark and the example changes with it. The example's age is the
 * person's to try.
 */
export const RacionadoPreview = ({ board, caption, unit, initialSample, cardFor }: RacionadoPreviewProps): ReactElement => {
  const [sample, setSample] = useState(String(initialSample))
  const value = markValue(sample)
  const card = value === null ? null : cardFor(value)

  return (
    <aside className={styles.preview} aria-label={`Vista previa de ${board}`}>
      <label className={styles.preview__caption}>
        <span>{caption}</span>
        <input
          className={styles.inline}
          inputMode="numeric"
          value={sample}
          aria-label={`Ejemplo de ${board} (${unit})`}
          onChange={(event) => setSample(event.target.value)}
        />
        <span>{unit}</span>
      </label>
      {card === null ? (
        <p className={styles.preview__empty}>Corrige los valores para ver el ejemplo.</p>
      ) : (
        <div className={`${styles.card} ${card.heat === undefined ? '' : styles[`card--${card.heat.level}`]}`}>
          <div className={styles.card__head}>
            <strong className={styles.card__title}>{card.title}</strong>
            {card.exclamations > 0 ? <span className={styles.card__bang}>{'!'.repeat(card.exclamations)}</span> : null}
          </div>
          <p className={styles.card__detail}>{card.detail}</p>
          <div className={styles.card__tags}>
            {card.late === true ? <span className={`${styles.card__tag} ${styles['card__tag--late']}`}>Tarde</span> : null}
            {card.heat === undefined ? null : <span className={styles.card__tag}>{LEVEL_LABEL[card.heat.level]}</span>}
            {card.band === undefined ? null : <span className={styles.card__tag}>{card.band}</span>}
          </div>
          {card.heat === undefined ? null : (
            <div className={styles.card__heat} aria-hidden="true">
              <span className={styles['card__heat-fill']} style={{ width: `${card.heat.value}%` }} />
            </div>
          )}
        </div>
      )}
    </aside>
  )
}
